/**
 * Un lien dans un message, et l'interstitiel de sortie (docs/13 §6).
 *
 * ⚠️ **L'interstitiel est un dévoilement, pas une page.** docs/13 §6 demande
 * « un interstitiel de sortie » ; l'implémenter comme une route
 * `/sortie?url=…` en ferait un lien légitime **vers n'importe où portant notre
 * domaine** — exactement l'outil qu'un hameçonnage cherche, et que rien
 * n'empêcherait ensuite d'envoyer par un autre canal que la messagerie. La
 * confirmation est donc rendue sur place, dans la bulle, et l'adresse affichée
 * est celle du lien lui-même.
 *
 * L'ancre reste une **vraie ancre** : le clic est intercepté, mais le
 * `href` est réel, si bien que « copier l'adresse du lien », le clic du milieu
 * et l'annonce d'un lecteur d'écran continuent de fonctionner. Un `<button>`
 * déguisé aurait supprimé les trois.
 *
 * `nofollow` en plus de `noopener noreferrer` : le corps d'un message est du
 * contenu écrit par un tiers, et une plateforme qui transmet son crédit de
 * référencement à ce qu'on lui fait écrire devient une cible de référencement.
 */
import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { isExternal } from "@/features/messaging/lib/markdown";

const LINK_CLASSES = "text-inherit underline underline-offset-2 break-all";

function hostOf(href: string): string {
  try {
    return new URL(href).host;
  } catch {
    return href;
  }
}

export function ExternalLink({ href }: { href: string }) {
  const { t } = useTranslation("messaging");
  const [leaving, setLeaving] = useState(false);

  // `window.location.origin` est lu au rendu et non figé dans un module : en
  // test comme en production, l'origine est celle de la page qui affiche le
  // message.
  if (!isExternal(href, window.location.origin)) {
    return (
      <a href={href} className={LINK_CLASSES}>
        {href}
      </a>
    );
  }

  return (
    <>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className={LINK_CLASSES}
        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
          // Seul le clic ordinaire est intercepté : un ctrl-clic ou un clic du
          // milieu ouvre déjà dans un onglet neuf, et le détourner ferait
          // disparaître un geste que l'utilisateur a choisi.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          setLeaving(true);
        }}
      >
        {/* Le libellé **est** l'adresse : docs/13 §6 demande que les domaines
            externes soient affichés en entier, et le parseur ne sait pas
            produire de lien étiqueté. */}
        {href}
      </a>
      {leaving ? (
        <span
          role="group"
          aria-label={t("link.title")}
          data-testid="external-link-prompt"
          className="mx-1 inline-flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
        >
          <span>{t("link.destination", { host: hostOf(href) })}</span>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="font-semibold underline underline-offset-2"
          >
            {t("link.open")}
          </a>
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => {
              setLeaving(false);
            }}
          >
            {t("link.cancel")}
          </button>
        </span>
      ) : null}
    </>
  );
}
